/**
 * Frontend permission helpers — mirrors api/internal/middleware/admin.go
 * `RequireRoleMin` semantics. Returning false here just hides/disables
 * UI; the backend is the source of truth and will 403 if a viewer
 * somehow hits a write endpoint anyway.
 *
 * Hierarchy: viewer < operator < admin < owner. Anything outside
 * (currently: 'agent', 'api') ranks at 0 and satisfies nothing.
 */

const ROLE_RANK: Record<string, number> = {
  viewer: 1,
  operator: 2,
  admin: 3,
  owner: 4,
};

export type AppRole = 'viewer' | 'operator' | 'admin' | 'owner';

export function roleAtLeast(have: string | undefined, min: AppRole): boolean {
  if (!have) return false;
  return (ROLE_RANK[have] ?? 0) >= ROLE_RANK[min];
}

/**
 * Convenience predicates. Add new ones here as the matrix grows so
 * components don't sprinkle role strings throughout the codebase.
 */
export const can = {
  // Workspace + team management — admin+
  editWorkspace:    (role?: string) => roleAtLeast(role, 'admin'),
  inviteMembers:    (role?: string) => roleAtLeast(role, 'admin'),
  manageSenders:    (role?: string) => roleAtLeast(role, 'admin'),
  manageApiKeys:    (role?: string) => roleAtLeast(role, 'admin'),
  // Admin module (reseller-only routes) — admin+ on top of tenant_type gate
  useAdminModule:   (role?: string) => roleAtLeast(role, 'admin'),

  // Day-to-day platform use — operator+
  sendMessages:     (role?: string) => roleAtLeast(role, 'operator'),
  manageContacts:   (role?: string) => roleAtLeast(role, 'operator'),
  launchCampaigns:  (role?: string) => roleAtLeast(role, 'operator'),

  // Voice module — admin+ to mutate (CRUD trunks / DIDs / flows / assets);
  // operator+ to read. Backend enforces, this just hides buttons.
  manageVoice:      (role?: string) => roleAtLeast(role, 'admin'),
  viewVoice:        (role?: string) => roleAtLeast(role, 'operator'),

  // Read-only — anyone authenticated (handled implicitly by JWT auth).
};
